import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {connect} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {copyGrey, eye, trash} from '../../assets/images/images';
import {
  DashboardLayout,
  Loader,
  PageLimit,
  Pagination,
  PrimaryButton,
  TableLayout,
  EmptyState,
} from '../../components';
import * as Actions from '../../store/actions';
import moment from 'moment/moment';
import {toast} from 'react-toastify';
import DeleteModal from './DeleteModal';
import WebhookSubscribeModal from './WebhookSubscribeModal';
import {parseUrlParams} from '../../helpers';
import {PageContext} from '../../helpers/context';
import {useQuery} from 'react-query';
import {fetchWebhookSubscriptions} from '../../requests/queries/Developers/webbhook';
import {useAllApps} from '../../hooks/useApps';
import PageNumber from 'components/PageNumber';

function DevelopersWebhook({getApiLogsFilters, getPortalUrl}) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [params, setParams] = useState({});
  const [apiParams, setApiParams] = useState({});
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);

  const {userApps} = useAllApps();

  const appParams = useMemo(
    () => ({
      ...apiParams,
      app_id: userApps?.map(app => app.value),
    }),
    [apiParams, userApps],
  );

  const {
    data: webhooks,
    isLoading,
    error,
  } = useQuery(
    ['webhook-sub', {params: {...appParams}}],
    () => fetchWebhookSubscriptions({params: {...appParams}}),
    {
      enabled: userApps?.length > 0,
      onError: error => {
        toast.error(error?.response?.data?.error || 'Failed to fetch webhooks');
      },
    },
  );

  const {setPageName} = useContext(PageContext);
  useEffect(() => {
    setPageName('webhooks');
  }, [setPageName]);

  const pageClick = selected => {
    setCurrentPage(selected);
  };

  const copyText = text => {
    navigator.clipboard.writeText(text);
    toast.success(`End point copied`);
  };

  const getAppName = useCallback(
    id => (userApps && userApps?.find(app => app?.value === id)?.name) || '',
    [userApps],
  );

  useEffect(() => {
    getApiLogsFilters();
  }, [getApiLogsFilters]);

  useEffect(() => {
    const apiParams = parseUrlParams();
    apiParams.limit = limit;

    const searchParams = new URLSearchParams(document.location.search);
    const pageParam = searchParams.get('page');
    if (pageParam) {
      setCurrentPage(parseInt(pageParam));
      apiParams.page = parseInt(pageParam);
    } else {
      apiParams.page = currentPage;
    }

    setApiParams(apiParams);
  }, [currentPage, limit]);

  return (
    <DashboardLayout
      breadCrumbs={
        <p className="text-tiny font-normal text-deepBlue">Webhooks</p>
      }
    >
      <DeleteModal
        bodyText="Please confirm your decision to delete this webhook. Once deleted, it cannot be recovered."
        open={showDeleteModal}
        setOpen={setShowDeleteModal}
        params={params}
        loading={isLoading}
      />
      <WebhookSubscribeModal
        open={showSubscribeModal}
        setOpen={setShowSubscribeModal}
      />
      {!isLoading && !error && webhooks?.subscriptions?.length === 0 && (
        <h2 className="text-grey text-base sm:text-lg font-medium">
          Webhooks Subscriptions
        </h2>
      )}

      {!isLoading && (error || webhooks?.subscriptions?.length === 0) ? (
        <EmptyState
          body={error ? 'Failed to fetch webhooks' : 'No hook(s) found'}
          height={75}
          buttonText="Subscribe"
          onClick={() => setShowSubscribeModal(true)}
        />
      ) : (
        <section className="flex flex-col">
          <div className="flex items-center justify-end gap-2 flex-wrap py-4 mx-[-32px] px-8 border-b border-[#E4E6E6]">
            <PrimaryButton
              buttonText="Subscribe"
              onClick={() => setShowSubscribeModal(true)}
            />
          </div>

          <TableLayout negativeMargins>
            <thead className="font-semibold uppercase">
              <tr className="bg-white80">
                <th className="pl-4 sm:pl-6 xl:pl-8 h-11 font-medium text-left text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>app name</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Environment</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>SERVICE TYPE</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>Date subscribed</span>
                  </div>
                </th>
                <th className="pl-0 font-medium text-left h-11 text-small text-[#5C688E] whitespace-nowrap">
                  <div className="flex items-end gap-1">
                    <span>end point</span>
                  </div>
                </th>
                <th className="min-w-[175px]"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={8}>
                    <Loader height={45} />
                  </td>
                </tr>
              )}
            </tbody>
            <tbody className="text-sm bg-white">
              {webhooks?.subscriptions?.map(subscription => (
                <tr
                  className="border-[0.5px] border-[#E4E3EC]"
                  key={subscription?.entity_id}
                >
                  <td className="p-4 pl-4 sm:pl-6 xl:pl-8 text-sm text-body whitespace-nowrap">
                    {getAppName(subscription?.app_id) || '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                    <div
                      className={`uppercase py-1 px-2 text-xs font-medium rounded-full flex items-center w-fit ${
                        subscription?.environment === 'live'
                          ? 'text-status-200 bg-statusBg-200'
                          : 'text-status-500 bg-statusBg-500'
                      }`}
                    >
                      <span className="-mb-[2px]">
                        {subscription?.environment === 'live'
                          ? 'live mode'
                          : subscription?.environment || '-'}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 pl-0 text-sm text-body uppercase whitespace-nowrap">
                    {subscription?.service?.replaceAll('_', ' ') || '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                    {moment(subscription?.date_created).format(
                      'Do MMM YYYY hh:mm',
                    )}
                  </td>
                  <td className="p-4 pl-0 text-sm whitespace-nowrap text-brandBlue">
                    {`${subscription?.endpoint?.substring(0, 26)}...` || '-'}
                  </td>
                  <td className="p-4 pl-0 text-sm text-body whitespace-nowrap">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => copyText(subscription?.endpoint)}
                        className="flex items-center relative bottom-[3px] px-2 text-grey80 bg-white80 pt-[9px] pb-[7px] rounded"
                      >
                        <img src={copyGrey} alt="" width={14} height={14} />
                        <span className="text-xs pl-1 font-medium relative top-[2px]">
                          Copy
                        </span>
                      </button>
                      <button
                        title="View"
                        className="h-4 cursor-pointer aspect-square"
                        onClick={() => {
                          getPortalUrl(
                            subscription?.convoy_app_id,
                            subscription?.convoy_subscription_id,
                          );
                          navigate(
                            `/developers/webhooks/${subscription.convoy_app_id}`,
                            {
                              state: {appId: subscription.convoy_app_id},
                            },
                          );
                        }}
                      >
                        <img src={eye} alt="" />
                      </button>
                      <button
                        title="Delete app"
                        onClick={() => {
                          setParams({
                            app_id: subscription.app_id,
                            service: subscription.service,
                          });
                          setShowDeleteModal(true);
                        }}
                        className="h-4 aspect-square"
                      >
                        <img src={trash} alt="" width={16} height={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableLayout>

          {!isLoading && webhooks?.subscriptions?.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-4 sm:gap-0 bg-white80 mx-[-32px] px-8 py-[18px] border-[0.5px] border-y-[#E4E3EC] border-transparent">
              <PageNumber
                currentPage={+currentPage}
                limit={limit}
                totalCount={(webhooks && webhooks?.totalRecords) || 0}
              />
              <div className="flex items-center gap-5">
                <PageLimit
                  onLimitChange={setLimit}
                  total={webhooks && webhooks?.totalRecords}
                  length={webhooks && webhooks?.subscriptions?.length}
                  limit={limit}
                />
                <Pagination
                  total={webhooks && Math.ceil(webhooks?.totalRecords / limit)}
                  current={+currentPage}
                  onPageChange={activePage => {
                    pageClick(activePage);
                    const searchParams = new URLSearchParams(
                      document.location.search,
                    );
                    searchParams.set('page', activePage);
                    const newSearchParams = searchParams.toString();
                    navigate(`?${newSearchParams}`);
                  }}
                />
              </div>
            </div>
          )}
        </section>
      )}
    </DashboardLayout>
  );
}

export default connect(state => state, Actions)(DevelopersWebhook);
